import React, { useEffect, useRef, useState } from 'react';
import { Building2 } from 'lucide-react';

const TrustedBySlider = ({ providers = [], title, subtitle }) => {
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);

  // Duplicate providers for seamless infinite scroll
  const duplicatedProviders = [...providers, ...providers, ...providers];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || providers.length === 0) return;

    let animationFrameId;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // Pixels per frame

    const animate = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed;
        
        // Reset scroll position for infinite loop
        const maxScroll = scrollContainer.scrollWidth / 3;
        if (scrollPosition >= maxScroll) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPaused, providers.length]);

  if (providers.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-teal-400"></div>
              <span className="text-sm font-semibold text-teal-600 uppercase tracking-wider">
                Our Partners
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-teal-400"></div>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {title || "Trusted by Leading Healthcare Providers"}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {subtitle || "Join hundreds of practices transforming patient care"}
          </p>
        </div>

        {/* Logo Slider */}
        <div className="relative">
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 via-gray-50/80 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 via-gray-50/80 to-transparent z-10 pointer-events-none"></div>

          {/* Scrolling Container */}
          <div
            ref={scrollRef}
            className="flex gap-8 overflow-hidden py-8"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            style={{ scrollBehavior: 'auto' }}
          >
            {duplicatedProviders.map((provider, index) => (
              <div
                key={`${provider.name}-${index}`}
                className="flex-shrink-0 group"
                style={{ width: '280px' }}
              >
                <div className="relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border-2 border-gray-100 hover:border-teal-200 h-full flex flex-col items-center justify-center">
                  {/* Logo Container */}
                  <div className="relative mb-6 w-full flex items-center justify-center">
                    {provider.logo_url ? (
                      <div className="relative w-full h-24 flex items-center justify-center">
                        <img
                          src={provider.logo_url}
                          alt={`${provider.name} logo`}
                          className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
                          style={{ filter: 'contrast(0.8) brightness(1.1)' }}
                        />
                        {/* Shine effect on hover */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 group-hover:translate-x-full transition-all duration-1000"></div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-teal-100 via-teal-50 to-blue-100 rounded-2xl flex items-center justify-center shadow-inner group-hover:shadow-lg group-hover:scale-110 transition-all duration-500">
                        <Building2 className="h-10 w-10 text-teal-600 group-hover:text-teal-700 transition-colors" />
                      </div>
                    )}
                  </div>

                  {/* Provider Info */}
                  <div className="text-center">
                    <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
                      {provider.name}
                    </h3>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-teal-50 to-blue-50 rounded-full">
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                      <p className="text-sm font-medium text-teal-700">
                        {provider.specialty}
                      </p>
                    </div>
                  </div>

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute top-4 right-4 w-3 h-3 border-t-2 border-r-2 border-teal-400 rounded-tr-lg"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-4 left-4 w-3 h-3 border-b-2 border-l-2 border-teal-400 rounded-bl-lg"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pause Indicator */}
          {isPaused && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-600">Paused - Move mouse away to continue</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TrustedBySlider;
